<template>
  <MainHeader />
  <MainSidebar />
  <div class="main-content bg_gray d-flex flex-column transition overflow-hidden">
    <BreadcrumbMenu pageTitle="Nueva Transferencia" />

    <div class="flex-grow-1 overflow-auto px-3 pb-5">

      <!-- Selector de modo -->
      <div class="d-flex gap-2 mb-3 align-items-center">
        <span class="small text-muted me-1">Modo:</span>
        <button class="btn btn-sm btn-secondary" disabled>Sistema Anterior</button>
        <button class="btn btn-sm btn-outline-secondary" disabled>
          Sistema Nuevo <span class="beta-badge">Beta</span>
          <span class="ms-1 text-muted small">(próximamente)</span>
        </button>
      </div>

      <!-- Cabecera -->
      <div class="card mb-3">
        <div class="card-body">
          <h6 class="card-title mb-3">Datos de la transferencia</h6>
          <div class="row g-2">
            <div class="col-md-3">
              <label class="form-label small fw-semibold mb-1">Origen <span class="text-danger">*</span></label>
              <select v-model="cab.idorigen" class="form-select form-select-sm" @change="onCambioAlmacen">
                <option :value="1">Lapoint 939</option>
                <option :value="15">Lapoint 1179</option>
              </select>
            </div>
            <div class="col-md-3">
              <label class="form-label small fw-semibold mb-1">Destino <span class="text-danger">*</span></label>
              <select v-model="cab.iddestino" class="form-select form-select-sm">
                <option v-for="a in almacenesDestino" :key="a.id" :value="a.id">{{ a.nombre }}</option>
              </select>
            </div>
            <div class="col-md-2">
              <label class="form-label small fw-semibold mb-1">Fecha <span class="text-danger">*</span></label>
              <input v-model="cab.fecha_emision" type="date" class="form-control form-control-sm" />
            </div>
            <div class="col-md-4">
              <label class="form-label small fw-semibold mb-1">Observación</label>
              <input v-model="cab.observacion" type="text" class="form-control form-control-sm"
                placeholder="Opcional" maxlength="300" />
            </div>
          </div>
        </div>
      </div>

      <!-- Buscador de productos -->
      <div class="card mb-3">
        <div class="card-body py-2">
          <div class="d-flex align-items-center gap-2">
            <div class="position-relative flex-grow-1" style="max-width:420px">
              <input ref="inputBuscar" v-model="busqueda" type="text"
                class="form-control form-control-sm"
                placeholder="Escanea barcode o escribe código de proveedor…"
                @keyup.enter="buscarProducto"
                @input="onInputBusqueda" />
              <ul v-if="sugerencias.length"
                class="list-group position-absolute w-100 shadow"
                style="z-index:200;top:100%;max-height:260px;overflow-y:auto">
                <li v-for="p in sugerencias" :key="p.idpres"
                  class="list-group-item list-group-item-action py-1 px-2 small"
                  style="cursor:pointer"
                  @mousedown.prevent="agregarProducto(p)">
                  <div class="d-flex justify-content-between">
                    <div>
                      <span class="badge bg-light text-dark border me-1" style="font-size:10px">{{ p.talla }}</span>
                      <span class="fw-semibold">{{ p.producto }}</span>
                    </div>
                    <code class="text-muted" style="font-size:10px">{{ p.codbarras }}</code>
                  </div>
                  <div class="text-muted" style="font-size:10px">{{ p.codigo_prov }}</div>
                </li>
              </ul>
            </div>
            <button class="btn btn-sm btn-outline-primary" @click="buscarProducto" :disabled="buscando">
              <span v-if="buscando" class="spinner-border spinner-border-sm"></span>
              <span v-else>Buscar</span>
            </button>
            <span v-if="errorBusqueda" class="text-danger small">{{ errorBusqueda }}</span>
          </div>
        </div>
      </div>

      <!-- Tabla de items -->
      <div class="card mb-3">
        <div class="card-header py-2 d-flex justify-content-between align-items-center">
          <span class="small fw-semibold">Productos a transferir</span>
          <span class="small text-muted">{{ items.length }} productos · {{ totalUnidades }} unidades</span>
        </div>
        <div class="card-body p-0">
          <div v-if="!items.length" class="text-center text-muted py-4 small">
            Busca un producto arriba para agregarlo
          </div>
          <div v-else class="table-responsive">
            <table class="table table-sm mb-0 align-middle" style="font-size:13px">
              <thead class="table-light">
                <tr>
                  <th class="ps-3">Producto</th>
                  <th class="text-center" style="width:70px">Talla</th>
                  <th style="width:130px">Barcode</th>
                  <th class="text-center" style="width:90px">Cantidad</th>
                  <th style="width:36px"></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(it, i) in items" :key="i">
                  <td class="ps-3">
                    <div class="fw-semibold" style="font-size:12px">{{ it.producto }}</div>
                    <div class="text-muted" style="font-size:11px">{{ it.codigo_prov }}</div>
                  </td>
                  <td class="text-center">
                    <span class="badge bg-light text-dark border">{{ it.talla }}</span>
                  </td>
                  <td><code class="small text-muted">{{ it.codbarras || '—' }}</code></td>
                  <td class="text-center">
                    <input v-model.number="it.cantidad" type="number" min="1"
                      class="form-control form-control-sm text-center p-1"
                      style="width:65px;font-size:12px" />
                  </td>
                  <td>
                    <button class="btn btn-xs btn-outline-danger p-0 px-1" @click="items.splice(i,1)" title="Quitar">✕</button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div v-if="error" class="alert alert-danger">{{ error }}</div>

    </div>

    <!-- Barra inferior -->
    <div style="position:sticky;bottom:0;z-index:10;background:#fff;border-top:1px solid #dee2e6;padding:10px 16px"
      class="d-flex justify-content-between align-items-center">
      <div class="small text-muted">
        <span v-if="items.length">{{ totalUnidades }} unidades en {{ items.length }} productos</span>
        <span v-else>Sin productos agregados</span>
      </div>
      <div class="d-flex gap-2">
        <button class="btn btn-sm btn-outline-secondary" @click="$router.push('/transfer-list')">Cancelar</button>
        <button class="btn btn-sm btn-outline-primary"
          :disabled="guardando || !puedeGuardar"
          @click="guardar(false)">
          <span v-if="guardando === 'borrador'" class="spinner-border spinner-border-sm me-1"></span>
          Guardar borrador
        </button>
        <button class="btn btn-sm btn-success"
          :disabled="guardando || !puedeGuardar"
          @click="guardar(true)">
          <span v-if="guardando === 'emitir'" class="spinner-border spinner-border-sm me-1"></span>
          Emitir transferencia
        </button>
      </div>
    </div>

  </div>
</template>

<script>
import MainHeader from '../../components/Layouts/MainHeader.vue';
import MainSidebar from '../../components/Layouts/MainSidebar.vue';
import BreadcrumbMenu from '../../components/Common/BreadcrumbMenu.vue';
import api from '../../services/api';

export default {
  name: 'CreateTransferPage',
  components: { MainHeader, MainSidebar, BreadcrumbMenu },

  data() {
    const hoy = new Date().toISOString().slice(0, 10);
    return {
      cab: {
        idorigen: 1,
        iddestino: 15,
        fecha_emision: hoy,
        observacion: '',
      },
      busqueda: '',
      sugerencias: [],
      buscando: false,
      errorBusqueda: null,
      busquedaTimer: null,
      items: [],
      guardando: false,
      error: null,
    };
  },

  computed: {
    almacenesDestino() {
      const todos = [
        { id: 1,  nombre: 'Lapoint 939' },
        { id: 15, nombre: 'Lapoint 1179' },
        { id: 16, nombre: 'Tránsito' },
      ];
      return todos.filter(a => a.id !== this.cab.idorigen);
    },
    totalUnidades() {
      return this.items.reduce((s, i) => s + (i.cantidad || 0), 0);
    },
    puedeGuardar() {
      return this.cab.idorigen && this.cab.iddestino &&
        this.cab.idorigen !== this.cab.iddestino &&
        this.cab.fecha_emision && this.items.length > 0;
    },
  },

  methods: {
    onCambioAlmacen() {
      if (this.cab.iddestino === this.cab.idorigen) {
        this.cab.iddestino = this.almacenesDestino[0]?.id || null;
      }
    },

    onInputBusqueda() {
      clearTimeout(this.busquedaTimer);
      this.errorBusqueda = null;
      if (this.busqueda.length < 2) { this.sugerencias = []; return; }
      this.busquedaTimer = setTimeout(() => this.buscarProducto(), 400);
    },
    async buscarProducto() {
      if (!this.busqueda.trim()) return;
      this.buscando = true;
      this.errorBusqueda = null;
      this.sugerencias = [];
      try {
        const r = await api.get('/productos/buscar', { params: { q: this.busqueda.trim(), limit: 20 } });
        const lista = r.data?.data || [];
        if (!lista.length) {
          this.errorBusqueda = 'No encontrado';
        } else if (lista.length === 1) {
          this.agregarProducto(lista[0]);
        } else {
          this.sugerencias = lista;
        }
      } catch (e) {
        this.errorBusqueda = e.response?.data?.error || 'Error al buscar';
      } finally {
        this.buscando = false;
      }
    },
    agregarProducto(prod) {
      this.sugerencias = [];
      this.busqueda = '';
      this.errorBusqueda = null;
      const existente = this.items.find(i => i.idpres === prod.idpres);
      if (existente) { existente.cantidad++; return; }
      this.items.push({
        idpres:      prod.idpres,
        producto:    prod.producto,
        talla:       prod.talla,
        codbarras:   prod.codbarras,
        codigo_prov: prod.codigo_prov,
        cantidad:    1,
      });
      this.$nextTick(() => this.$refs.inputBuscar?.focus());
    },

    async guardar(emitir) {
      this.error = null;
      this.guardando = emitir ? 'emitir' : 'borrador';
      try {
        const payload = {
          idorigen:      this.cab.idorigen,
          iddestino:     this.cab.iddestino,
          fecha_emision: this.cab.fecha_emision,
          observacion:   this.cab.observacion || '',
          items: this.items.map(i => ({
            idpres:    i.idpres,
            cantidad:  i.cantidad,
          })),
        };
        const r = await api.post('/transferencias', payload);
        const idreg = r.data?.data?.idreg;

        if (emitir && idreg) {
          await api.post(`/transferencias/${idreg}/emitir`);
        }

        this.$router.push('/transfer-list');
      } catch (e) {
        const d = e.response?.data;
        this.error = d?.details?.map(x => x.msg).join(' · ') || d?.error || e.message;
      } finally {
        this.guardando = false;
      }
    },
  },
};
</script>

<style scoped>
.beta-badge {
  display: inline-block;
  margin-left: 6px;
  padding: 1px 5px;
  font-size: 10px;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #6366f1, #4f46e5);
  border-radius: 4px;
  vertical-align: middle;
  line-height: 1.4;
  text-transform: uppercase;
}
</style>
